import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListX } from "lucide-react";

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Mono,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@lagoon/ui";

import { api, type DenylistEntry, type DenylistKind, type Me } from "../api.js";
import { ReasonAction } from "../components/reason-action.js";

/**
 * The names the platform will not hand out, and the names it watches for.
 *
 * The screen exists because the list has to change on a phishing kit's schedule
 * rather than on a release's (`docs/CONTENT-SCREENING-PLAN.md` §6). Everything
 * on it is audited, and adding or removing a term is an administrator action
 * rather than an operator one: it is a policy change that affects every future
 * customer, not a response to an incident in front of you.
 *
 * The two kinds do different jobs and the screen says so, because an operator
 * choosing the wrong one gets a rule that either never fires or fires on
 * everything.
 */
export function DenylistScreen({ me }: { me: Me }): React.JSX.Element {
  const [kind, setKind] = useState<DenylistKind | "all">("all");

  const entries = useQuery({
    queryKey: ["denylist", kind],
    queryFn: () => api.denylist(kind === "all" ? undefined : kind),
  });

  const columns = useEntryColumns(me.can.administer);

  return (
    <div className="lg:flex lg:flex-col lg:gap-8">
      <PageHeader
        eyebrow="Safety"
        title="Name denylist"
        description="The names the platform refuses, and the brands the upload heuristics watch for on a page."
        {...(me.can.administer ? { actions: <AddTerm /> } : {})}
      />

      <Alert tone="info">
        <AlertTitle>Which kind to use</AlertTitle>
        <AlertDescription>
          <p>
            <strong>slug</strong> terms are refused as site names on an exact match, and flag a
            look-alike for review. <strong>brand</strong> terms do that too, and are additionally
            what the upload heuristics look for on a page: a brand name beside a password field or a
            credential prompt flags the release. Keep everyday words like &ldquo;login&rdquo; out of{" "}
            <strong>brand</strong>: they appear on ordinary sites, and a rule that fires on
            everything is a queue nobody reads.
          </p>
          <p className="lg:mt-2">
            This list is the editable half. The platform&rsquo;s own hostnames (app, mcp, admin and
            the rest) are a constant in the code and cannot be removed from here.
          </p>
        </AlertDescription>
      </Alert>

      <div className="lg:flex lg:flex-wrap lg:gap-2">
        {(["all", "slug", "brand"] as const).map((option) => (
          <Button
            key={option}
            variant={kind === option ? "subtle" : "ghost"}
            size="sm"
            aria-pressed={kind === option}
            onClick={() => {
              setKind(option);
            }}
          >
            {option}
          </Button>
        ))}
      </div>

      {entries.error && (
        <Text size="sm" tone="danger" role="alert">
          {entries.error.message}
        </Text>
      )}

      <DataTable
        columns={columns}
        data={entries.data?.entries ?? []}
        loading={entries.isPending}
        caption="Denylisted names"
        empty={
          <EmptyState
            icon={<ListX />}
            title="Nothing on the list"
            description="No name is being refused beyond the platform's own reserved hostnames."
            glow={false}
          />
        }
      />

      {!me.can.administer && (
        <Text size="sm" tone="muted">
          Editing the denylist is an administrator action: it changes what names every future
          customer can take.
        </Text>
      )}
    </div>
  );
}

function useEntryColumns(canAdminister: boolean): DataTableColumn<DenylistEntry>[] {
  return useMemo(() => {
    const columns: DataTableColumn<DenylistEntry>[] = [
      {
        accessorKey: "kind",
        header: "Kind",
        cell: (info) => (
          <Badge tone={info.row.original.kind === "brand" ? "accent" : "neutral"}>
            {info.row.original.kind}
          </Badge>
        ),
      },
      {
        accessorKey: "term",
        header: "Term",
        cell: (info) => <Mono className="lg:text-strong">{info.row.original.term}</Mono>,
      },
      {
        accessorKey: "note",
        header: "Note",
        cell: (info) => (
          <Text size="caption" tone="muted" className="lg:max-w-none">
            {info.row.original.note ?? ""}
          </Text>
        ),
      },
      {
        accessorKey: "createdBy",
        header: "Added by",
        cell: (info) => (
          <Mono className="lg:text-caption lg:text-muted-foreground">
            {info.row.original.createdBy ?? ""}
          </Mono>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Added",
        sortFn: "datetime",
        cell: (info) => (
          <Mono className="lg:text-caption lg:text-muted-foreground">
            {new Date(info.row.original.createdAt).toLocaleDateString()}
          </Mono>
        ),
      },
    ];

    if (canAdminister) {
      columns.push({
        id: "actions",
        header: "Actions",
        cell: (info) => <RemoveTerm id={info.row.original.id} term={info.row.original.term} />,
      });
    }

    return columns;
  }, [canAdminister]);
}

function RemoveTerm({ id, term }: { id: string; term: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  return (
    <ReasonAction
      label="Remove"
      compact
      hint={`Stop refusing "${term}". The row and this reason are kept.`}
      onSubmit={async (reason) => {
        await api.removeDenylistTerm(id, reason);
        await queryClient.invalidateQueries({ queryKey: ["denylist"] });
      }}
    />
  );
}

/**
 * Add a term. Two steps like every other mutation here, and the reason field is
 * what a person reads in six months when they wonder whether it can go.
 */
function AddTerm(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<DenylistKind>("brand");
  const [term, setTerm] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<{ field: "term" | "reason"; message: string } | null>(
    null,
  );
  const queryClient = useQueryClient();

  const run = useMutation({
    mutationFn: () =>
      api.addDenylistTerm({
        kind,
        term: term.trim(),
        ...(note.trim() ? { note: note.trim() } : {}),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["denylist"] });
      setOpen(false);
      setTerm("");
      setNote("");
      setReason("");
      setProblem(null);
    },
  });

  const confirm = (): void => {
    if (term.trim().length < 2) {
      setProblem({
        field: "term",
        message: "A term of one character would refuse half the alphabet.",
      });
      return;
    }
    if (reason.trim().length < 3) {
      setProblem({
        field: "reason",
        message: "Say why. This is what an auditor reads in six months.",
      });
      return;
    }
    setProblem(null);
    run.mutate();
  };

  return (
    <>
      <Button
        onClick={() => {
          setOpen(true);
        }}
      >
        Add a term
      </Button>

      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setProblem(null);
        }}
        title="Add a denylist term"
        description="Refuses the name for every future customer. Audited, against your name."
        confirmLabel={run.isPending ? "Working…" : "Add"}
        onConfirm={confirm}
        pending={run.isPending}
      >
        <div className="lg:flex lg:flex-col lg:gap-4">
          <FormField label="Kind" required>
            <Select
              value={kind}
              onValueChange={(next) => {
                // Narrowed rather than asserted: the value comes back from
                // Radix as a plain string, and a cast would let a renamed
                // SelectItem reach the API as a kind that does not exist.
                if (next === "brand" || next === "slug") setKind(next);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="brand">brand</SelectItem>
                <SelectItem value="slug">slug</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            label="Term"
            required
            hint="Lowercase, no spaces."
            {...(problem?.field === "term" ? { error: problem.message } : {})}
          >
            <Input
              value={term}
              autoFocus
              onChange={(event) => {
                setTerm(event.target.value);
              }}
            />
          </FormField>

          <FormField label="Note" hint="Optional: why this is on the list.">
            <Input
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </FormField>

          <FormField
            label="Reason"
            required
            hint="Recorded in the audit trail."
            {...(problem?.field === "reason" ? { error: problem.message } : {})}
          >
            <Input
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </FormField>

          {run.error && (
            <Text size="sm" tone="danger" role="alert">
              {run.error.message}
            </Text>
          )}
        </div>
      </ConfirmDialog>
    </>
  );
}
